import {inject, Injectable, signal, WritableSignal} from '@angular/core';
import {firstValueFrom, Observable} from 'rxjs';
import {AuthMethod, AuthTransitionStatus, IUser, LoginResponse, Place, TimelineEntry, User} from "../interfaces";
import {ApiService} from "./api.service";
import {StorageService} from "./storage.service";
import {SettingsRepository} from "@app/repositories/settings.repository";
import {flare} from "@flareapp/flare-client";
import {environment} from "@env/environment";

interface MagicLinkAuthenticationResponse {
  plainTextToken: string;
  accessToken?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService extends ApiService {
  public currentAccessToken: WritableSignal<null | string> = signal(null);
  public currentUser: WritableSignal<User | null> = signal(null);
  public isAuthenticated: WritableSignal<boolean> = signal(false);
  public canCreateNews: WritableSignal<boolean> = signal(false);
  public canCreateEvents: WritableSignal<boolean> = signal(false);
  public canCreatePlaces: WritableSignal<boolean> = signal(false);
  public fcmToken: WritableSignal<string> = signal('');
  public authMethod: WritableSignal<AuthMethod> = signal(null);
  public authTransitionStatus: WritableSignal<AuthTransitionStatus> = signal('idle');

  private storage = inject(StorageService);
  private settings = inject(SettingsRepository);
  private sessionLoadPromise: Promise<User | null> | null = null;

  constructor() {
    super();
    console.log(`${this.constructor.name}.constructor`);
  }

  canUpdate(model: TimelineEntry) {
    if (!model) {
      console.warn('No timeline entry');
    }
    const user = this.currentUser();
    if (!user) {
      return false;
    }

    return user.places.map(place => place.id).includes(model.owner.id);
  }

  canUpdatePlace(place: Place | null | undefined): boolean {
    if (!place) {
      return false;
    }

    const user = this.currentUser();
    if (!user) {
      return false;
    }

    return user.ownsPlace(place);
  }

  login(email: string, password: string, deviceName: string | null = null): Observable<LoginResponse> {
    return this.post(`/user/authenticate`, {
      email: email,
      password: password,
      device_name: deviceName,
    });
  }

  async logout(): Promise<void> {
    await this.clearPersistedAuthState();
    this.clearRuntimeAuthState();
    this.setAuthTransitionStatus('idle');
  }

  fetch(): Observable<IUser> {
    return this.get(`/user?include=settings`);
  }

  authenticateWithMagicLink(searchString: string): Observable<MagicLinkAuthenticationResponse> {
    return this.post(`/user/authenticate-by-login-link${searchString}`, []);
  }

  requestMagicLink(email: string, redirectUrl: string | null = null): Promise<void> {
    this.setAuthTransitionStatus('requesting-magic-link');

    return firstValueFrom(this.post<void>(`/user/request-magic-link`, {
      email,
      app_url: this.getMagicLinkCallbackUrl(redirectUrl),
    })).then(() => {
      this.setAuthTransitionStatus('idle');
    }).catch((error: unknown) => {
      this.reportMagicLinkProblem('request_failed', error, {redirectUrl, email});
      this.setAuthTransitionStatus('idle');
      throw error;
    });
  }

  async authenticateByMagicLink(token: string, email: string): Promise<boolean> {
    this.setAuthTransitionStatus('verifying-magic-link');

    try {
      const response = await firstValueFrom(this.post<MagicLinkAuthenticationResponse>(`/user/authenticate-by-magic-link`, {
        token,
        email,
        fcm_token: localStorage.getItem('fcm_token') ?? this.fcmToken() ?? undefined,
      }));

      if (!response?.plainTextToken) {
        this.reportMagicLinkProblem('verify_missing_token', new Error('No access token received from magic-link authentication'), {
          email,
          tokenPresent: !!token,
        });
        this.setAuthTransitionStatus('idle');
        return false;
      }

      await this.setAccessToken(response.plainTextToken, 'magic-link');
      return true;
    } catch (error: unknown) {
      this.reportMagicLinkProblem('verify_failed', error, {
        email,
        tokenPresent: !!token,
      });
      this.setAuthTransitionStatus('idle');
      throw error;
    }
  }

  updateUser(user: User) {
    this.currentUser.set(user);
    if (user.fcm_token) {
      this.fcmToken.set(user.fcm_token);
    }

    this.settings.setUser(user);
    console.log(this.constructor.name + '.updateUser: sync user', user.email);
  }

  checkAuthentication() {
    const params = new URLSearchParams(window.location.search);

    if (params.has('mlt') && params.has('expires') && params.has('signature')) {
      return this.loginWithMagicLink(document.location.search);
    }

    return this.checkAccessToken();
  }

  async loginWithMagicLink(searchString: string) {
    let response = await firstValueFrom(this.authenticateWithMagicLink(searchString)).catch(() => null);
    if (!response) {
      return;
    }

    if (response.plainTextToken) {
      await this.setAccessToken(response.plainTextToken, 'magic-link');
    }
  }

  checkAccessToken(): Promise<User | null> {
    return this.ensureSessionLoaded();
  }

  setAccessToken(accessToken: string, authMethod: AuthMethod = 'password'): Promise<User> {
    const normalizedAccessToken = this.normalizeAccessToken(accessToken);

    const hydrationPromise = this.hydrateSessionWithToken(normalizedAccessToken, authMethod);
    this.sessionLoadPromise = hydrationPromise.finally(() => {
      this.sessionLoadPromise = null;
    });

    return hydrationPromise;
  }

  ensureSessionLoaded(): Promise<User | null> {
    if (this.isAuthenticated() && this.currentUser()) {
      return Promise.resolve(this.currentUser());
    }

    if (this.sessionLoadPromise) {
      return this.sessionLoadPromise;
    }

    this.sessionLoadPromise = this.restorePersistedSession().finally(() => {
      this.sessionLoadPromise = null;
    });

    return this.sessionLoadPromise;
  }

  getDefaultAuthenticatedRoute(): string {
    return '/user/details';
  }

  private setAuthMethod(authMethod: AuthMethod): void {
    this.authMethod.set(authMethod);
    if (authMethod === null) {
      this.settings.setAuthMethod(null);
      return;
    }

    this.settings.setAuthMethod(authMethod);
  }

  private setAuthTransitionStatus(status: AuthTransitionStatus): void {
    this.authTransitionStatus.set(status);
    this.settings.setAuthTransitionStatus(status);
  }

  private getMagicLinkCallbackUrl(redirectUrl: string | null): string {
    const callbackUrl = new URL('/auth/magic-link', environment.appBaseUrl);

    if (redirectUrl) {
      callbackUrl.searchParams.set('redirectUrl', redirectUrl);
    }

    return callbackUrl.toString();
  }

  private reportMagicLinkProblem(event: string, error: unknown, context: Record<string, unknown> = {}): void {
    flare.report(this.toError(error), {
      magicLinkEvent: event,
      status: this.extractStatusCode(error),
      ...context,
    });
  }

  private toError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }

    if (typeof error === 'string') {
      return new Error(error);
    }

    if (typeof error === 'object' && error !== null && 'error' in error) {
      const nestedError = (error as {error?: {message?: string}}).error;
      if (nestedError?.message) {
        return new Error(nestedError.message);
      }
    }

    return new Error('Unknown magic-link error');
  }

  private extractStatusCode(error: unknown): number | null {
    if (typeof error === 'object' && error !== null && 'status' in error && typeof (error as {status?: unknown}).status === 'number') {
      return (error as {status: number}).status;
    }

    return null;
  }

  private async restorePersistedSession(): Promise<User | null> {
    const {token, authMethod} = await this.getPersistedToken();

    if (!token) {
      console.log(`${this.constructor.name}.restorePersistedSession: no persisted token found`);
      this.settings.removeUser();
      this.settings.resetAuthState();
      this.clearRuntimeAuthState();
      this.setAuthTransitionStatus('idle');
      return null;
    }

    console.log(`${this.constructor.name}.restorePersistedSession: restoring persisted session`);
    return this.hydrateSessionWithToken(token, authMethod ?? 'password');
  }

  private async hydrateSessionWithToken(accessToken: string, authMethod: AuthMethod): Promise<User> {
    this.setAuthTransitionStatus('authenticating');
    this.currentAccessToken.set(accessToken);
    this.settings.setAccessToken(accessToken);
    this.setAuthMethod(authMethod);

    try {
      const data = await firstValueFrom(this.fetch());
      const user = new User(data);
      this.applyAuthenticatedUser(user);
      this.setAuthTransitionStatus('idle');
      flare.addContext('user', user.id);

      return user;
    } catch (error: unknown) {
      console.warn(`${this.constructor.name}.hydrateSessionWithToken`, 'error while hydrating session:', error);
      await this.handleSessionHydrationError(error);
      this.setAuthTransitionStatus('idle');
      throw error;
    }
  }

  private applyAuthenticatedUser(user: User): void {
    this.updateUser(user);
    this.isAuthenticated.set(true);
    this.canCreatePlaces.set(true);
    this.canCreateNews.set(user.places.length > 0);
    this.canCreateEvents.set(user.places.length > 0);
  }

  private clearRuntimeAuthState(): void {
    this.currentUser.set(null);
    this.currentAccessToken.set(null);
    this.isAuthenticated.set(false);
    this.canCreateNews.set(false);
    this.canCreateEvents.set(false);
    this.canCreatePlaces.set(false);
    this.authMethod.set(null);
  }

  private async clearPersistedAuthState(): Promise<void> {
    await this.storage.remove('user');
    await this.storage.remove('accessToken');
    this.settings.removeUser();
    this.settings.resetAuthState();
  }

  private async getPersistedToken(): Promise<{ token: string | null; authMethod: AuthMethod }> {
    const settings = await firstValueFrom(this.settings.settings$);
    const accessToken = settings.accessToken ?? await this.storage.get('accessToken');

    return {
      token: accessToken ? this.normalizeAccessToken(accessToken) : null,
      authMethod: settings.authMethod ?? 'password',
    };
  }

  private async handleSessionHydrationError(error: unknown): Promise<void> {
    if (this.extractStatusCode(error) === 401) {
      await this.clearPersistedAuthState();
    }

    this.clearRuntimeAuthState();
  }

  private normalizeAccessToken(accessToken: string): string {
    if (accessToken.indexOf('Bearer') > -1 || accessToken.indexOf('|') > -1) {
      return <string>accessToken.split('|').pop();
    }

    return accessToken;
  }
}
